import { Extension } from "@tiptap/core";
import { Node } from "@tiptap/pm/model";
import { Plugin } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

type DecorationsProp = NonNullable<
  NonNullable<ConstructorParameters<typeof Plugin>[0]["props"]>["decorations"]
>;
type PluginDecorationSource = NonNullable<ReturnType<DecorationsProp>>;

const toPluginDecorationSource = (decorationSet: DecorationSet): PluginDecorationSource =>
  decorationSet as unknown as PluginDecorationSource;

export type InputVariableDecorationState = "filled" | "unfilled" | "no-set";

export interface InputVariableDecoration {
  state: InputVariableDecorationState;
  preview?: string;
}

interface DecorationContext {
  text: string;
  pos: number;
  node: Node;
  decorations: Decoration[];
  isInsideVariable: (text: string, position: number) => boolean;
  inputVariableDecorations: Record<string, InputVariableDecoration>;
}

interface ExtensionOptions {
  mode: "jinja2" | "fstring";
  isOutput: boolean;
  markdownEnabled: boolean;
  inputVariableDecorations: Record<string, InputVariableDecoration>;
  getInputVariableDecorations?: () => Record<string, InputVariableDecoration>;
}

const createVariableChecker =
  (mode: ExtensionOptions["mode"]) =>
  (text: string, position: number): boolean => {
    if (mode === "jinja2") {
      const variableRegex = /\{\{(?!\{)[^{}]*(?:\{[^{}]*\}[^{}]*)*\}\}(?!\})/g;
      let match;
      while ((match = variableRegex.exec(text)) !== null) {
        if (position >= match.index && position < match.index + match[0].length) {
          return true;
        }
      }
    } else {
      const variableRegex = /\{([^{}]+)\}/g;
      let match;
      while ((match = variableRegex.exec(text)) !== null) {
        if (position >= match.index && position < match.index + match[0].length) {
          return true;
        }
      }
    }
    return false;
  };

const normalizeInputVariableKey = (rawVariable: string, mode: ExtensionOptions["mode"]): string => {
  const braceCount = mode === "jinja2" ? 2 : 1;
  return rawVariable.slice(braceCount, -braceCount).trim();
};

const cnInputVariableClass = (state?: InputVariableDecorationState) => {
  if (!state) {
    return "input-variable";
  }

  return `input-variable input-variable--${state}`;
};

type InputVariableSegment = "start" | "body" | "end";

const getInputVariableDecorationAttrs = (
  key: string,
  inputVariableDecorations: Record<string, InputVariableDecoration>,
  segment?: InputVariableSegment,
) => {
  const decoration = inputVariableDecorations[key];
  const state = decoration?.state;
  const segmentClass = segment ? ` input-variable--${segment}` : "";

  return {
    class: `${cnInputVariableClass(state)}${segmentClass}`,
    "data-input-variable": "true",
    "data-input-variable-key": key,
    ...(state ? { "data-input-variable-state": state } : {}),
    // Preview belongs on the closing segment so fragmented decorations do not repeat it.
    ...(decoration?.preview && segment !== "start" && segment !== "body"
      ? { "data-input-variable-preview": decoration.preview }
      : {}),
  };
};

const MarkdownDecorators = {
  bold: (context: DecorationContext): void => {
    const { text, pos, decorations, isInsideVariable } = context;
    const boldRegex = /(\*\*|__)(.+?)\1/g;
    let match;

    while ((match = boldRegex.exec(text)) !== null) {
      const matchStart = match.index;

      if (isInsideVariable(text, matchStart)) {
        continue;
      }

      const start = pos + match.index;
      const end = start + match[0].length;

      decorations.push(
        Decoration.inline(start, start + match[1].length, {
          class: "markdown-marker",
          "data-markdown-marker": "true",
        }),
      );

      decorations.push(
        Decoration.inline(end - match[1].length, end, {
          class: "markdown-marker",
          "data-markdown-marker": "true",
        }),
      );

      const innerStart = start + match[1].length;
      const innerEnd = end - match[1].length;

      decorations.push(
        Decoration.inline(innerStart, innerEnd, {
          class: "markdown-bold",
          "data-markdown-bold": "true",
        }),
      );
    }
  },

  boldItalic: (context: DecorationContext): void => {
    const { text, pos, decorations, isInsideVariable } = context;
    const boldItalicRegex = /\*\*\*(.+?)\*\*\*/g;
    let match;

    while ((match = boldItalicRegex.exec(text)) !== null) {
      const matchStart = match.index;

      if (isInsideVariable(text, matchStart)) {
        continue;
      }

      const start = pos + match.index;
      const end = start + match[0].length;

      decorations.push(
        Decoration.inline(start, start + 3, {
          class: "markdown-marker",
          "data-markdown-marker": "true",
        }),
      );

      decorations.push(
        Decoration.inline(end - 3, end, {
          class: "markdown-marker",
          "data-markdown-marker": "true",
        }),
      );

      const innerStart = start + 3;
      const innerEnd = end - 3;
      decorations.push(
        Decoration.inline(innerStart, innerEnd, {
          class: "markdown-bold markdown-italic",
          "data-markdown-bold": "true",
          "data-markdown-italic": "true",
        }),
      );
    }
  },

  italic: (context: DecorationContext): void => {
    const { text, pos, decorations, isInsideVariable } = context;

    const italicRegex = /(?<!\*)\*(?!\*)([^*]+?)(?<!\*)\*(?!\*)/g;
    let match;

    while ((match = italicRegex.exec(text)) !== null) {
      const matchStart = match.index;

      if (isInsideVariable(text, matchStart)) {
        continue;
      }

      const start = pos + match.index;
      const end = start + match[0].length;
      const innerStart = start + 1;
      const innerEnd = end - 1;

      decorations.push(
        Decoration.inline(start, start + 1, {
          class: "markdown-marker",
          "data-markdown-marker": "true",
        }),
      );

      decorations.push(
        Decoration.inline(end - 1, end, {
          class: "markdown-marker",
          "data-markdown-marker": "true",
        }),
      );

      decorations.push(
        Decoration.inline(innerStart, innerEnd, {
          class: "markdown-italic",
          "data-markdown-italic": "true",
        }),
      );
    }
  },
};

const HeadingDecorator = {
  process: (node: Node, pos: number, decorations: Decoration[]): void => {
    if (!node.isTextblock) {
      return;
    }

    const text = node.textContent;
    const match = text.match(/^(#{1,6}) (.*)/);
    if (!match) {
      return;
    }

    const hashes = match[1];
    const level = Math.min(hashes.length, 6);
    const headingText = match[2];

    let currentPos = pos + 1;

    node.content.forEach((child, index) => {
      if (child.isText) {
        const childText = child.text || "";
        const childStart = currentPos;
        const childEnd = currentPos + childText.length;

        if (index === 0) {
          const hashMatch = childText.match(/^(#{1,6}) (.*)/);
          if (hashMatch) {
            const [, hashPart, restText] = hashMatch;

            decorations.push(
              Decoration.inline(childStart, childStart + hashPart.length, {
                class: `markdown-marker markdown-heading markdown-heading-${level}`,
                "data-markdown-hash": "true",
              }),
            );

            decorations.push(
              Decoration.inline(childStart + hashPart.length, childStart + hashPart.length + 1, {
                class: `markdown-heading-${level}`,
                "data-markdown-space": "true",
              }),
            );

            if (restText) {
              decorations.push(
                Decoration.inline(childStart + hashPart.length + 1, childEnd, {
                  class: `markdown-heading markdown-heading-${level}`,
                  "data-markdown-heading": "true",
                  "data-heading-level": String(level),
                }),
              );
            }
          }
        } else {
          decorations.push(
            Decoration.inline(childStart, childEnd, {
              class: `markdown-heading markdown-heading-${level}`,
              "data-markdown-heading": "true",
              "data-heading-level": String(level),
            }),
          );
        }
      }

      currentPos += child.nodeSize;
    });

    if (!headingText.trim()) {
      decorations.push(
        Decoration.widget(
          pos + 1 + hashes.length + 1,
          () => {
            const span = document.createElement("span");
            span.className = `markdown-heading markdown-heading-${level}`;
            span.textContent = `Heading ${level}`;
            span.style.opacity = "0.2";
            span.style.pointerEvents = "none";
            return span;
          },
          { side: 1 },
        ),
      );
    }
  },
};

const TemplateDecorators = {
  jinja2: {
    variables: (context: DecorationContext): void => {
      const { text, pos, decorations, inputVariableDecorations } = context;
      const variableRegex = /\{\{(?!\{)[^{}]*(?:\{[^{}]*\}[^{}]*)*\}\}(?!\})/g;
      let match;

      while ((match = variableRegex.exec(text)) !== null) {
        const start = pos + match.index;
        const end = start + match[0].length;
        const key = normalizeInputVariableKey(match[0], "jinja2");

        // Emit a single inline decoration spanning the whole `{{ ... }}` so each
        // variable is one hover/click target (a single padded, rounded pill)
        // instead of three separate brace/body/end segment spans. The old split
        // was defensive against a future decorator fragmenting the body (e.g.
        // per-filter highlighting); none does today, so keep it collapsed and
        // reintroduce start/body/end only if such fragmentation is added.
        decorations.push(
          Decoration.inline(
            start,
            end,
            getInputVariableDecorationAttrs(key, inputVariableDecorations),
          ),
        );
      }
    },

    tags: (context: DecorationContext): void => {
      const { text, pos, decorations } = context;
      const tagRegex = /\{%(?:[^%]|(?:%(?!\})))*%\}/g;
      let match;

      while ((match = tagRegex.exec(text)) !== null) {
        const start = pos + match.index;
        const end = start + match[0].length;

        decorations.push(
          Decoration.inline(start, start + 2, {
            class: "jinja2-tag-delimiter",
            "data-jinja2-tag-delimiter": "true",
          }),
        );

        decorations.push(
          Decoration.inline(end - 2, end, {
            class: "jinja2-tag-delimiter",
            "data-jinja2-tag-delimiter": "true",
          }),
        );

        const innerContent = match[0].slice(2, -2).trim();
        const innerStart = start + 2;
        const innerEnd = end - 2;

        if (innerEnd > innerStart) {
          const controlKeywords =
            /^\s*(if|for|while|with|set|block|extends|include|import|from|macro|call|filter|raw|endraw|endif|endfor|endwhile|endwith|endblock|endmacro|endcall|endfilter)\b/;

          if (controlKeywords.test(innerContent)) {
            decorations.push(
              Decoration.inline(innerStart, innerEnd, {
                class: "jinja2-control",
                "data-jinja2-control": "true",
              }),
            );
          } else {
            decorations.push(
              Decoration.inline(innerStart, innerEnd, {
                class: "jinja2-tag",
                "data-jinja2-tag": "true",
              }),
            );
          }
        }
      }
    },

    comments: (context: DecorationContext): void => {
      const { text, pos, decorations } = context;
      const commentRegex = /\{#(?:[^#]|(?:#(?!\})))*#\}/g;
      let match;

      while ((match = commentRegex.exec(text)) !== null) {
        const start = pos + match.index;
        const end = start + match[0].length;

        decorations.push(
          Decoration.inline(start, end, {
            class: "jinja2-comment",
            "data-jinja2-comment": "true",
          }),
        );
      }
    },
  },

  fstring: {
    variables: (context: DecorationContext): void => {
      const { text, pos, decorations, inputVariableDecorations } = context;
      const variableRegex = /\{([^{}]+)\}/g;
      let match;

      while ((match = variableRegex.exec(text)) !== null) {
        const start = pos + match.index;
        const end = start + match[0].length;
        const key = normalizeInputVariableKey(match[0], "fstring");

        decorations.push(
          Decoration.inline(
            start,
            end,
            getInputVariableDecorationAttrs(key, inputVariableDecorations),
          ),
        );
      }
    },
  },
};

const MarkdownStyleDecoration = Extension.create({
  name: "markdownStyleDecoration",

  addOptions() {
    return {
      mode: "jinja2",
      isOutput: false,
      markdownEnabled: true,
      inputVariableDecorations: {},
      getInputVariableDecorations: undefined,
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          decorations: state => {
            const decorations: Decoration[] = [];
            const isInsideVariable = createVariableChecker(this.options.mode);

            state.doc.descendants((node, pos) => {
              if (!node.isText) {
                return;
              }

              const text = node.text ?? "";
              const context: DecorationContext = {
                text,
                pos,
                node,
                decorations,
                isInsideVariable,
                inputVariableDecorations:
                  this.options.getInputVariableDecorations?.() ??
                  this.options.inputVariableDecorations,
              };

              if (this.options.markdownEnabled) {
                MarkdownDecorators.bold(context);
                MarkdownDecorators.boldItalic(context);
                MarkdownDecorators.italic(context);
              }

              if (!this.options.isOutput) {
                if (this.options.mode === "jinja2") {
                  TemplateDecorators.jinja2.variables(context);
                  TemplateDecorators.jinja2.tags(context);
                  TemplateDecorators.jinja2.comments(context);
                } else {
                  TemplateDecorators.fstring.variables(context);
                }
              }
            });

            if (this.options.markdownEnabled) {
              state.doc.descendants((node, pos) => {
                HeadingDecorator.process(node, pos, decorations);
              });
            }
            return toPluginDecorationSource(DecorationSet.create(state.doc, decorations));
          },
        },
      }),
    ];
  },
});

export default MarkdownStyleDecoration;
